"use client";
import React, { useState, useEffect } from "react";
import {
  Card,
  Typography,
  Box,
  Tabs,
  Tab,
  Button,
  Stack,
  Snackbar,
  Fade,
} from "@mui/material";
import ClientInfo from "./ClientInfo";
import ClientCredentials from "./ClientCredentials";
import SuccessDialog from "./SuccessDialog";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams } from "react-router-dom";
import config from "../../../../config";

function CustomTabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ p: 3 }}>{children}</Box> : null;
}

function a11yProps(index) {
  return {
    id: `client-tab-${index}`,
    "aria-controls": `client-tabpanel-${index}`,
  };
}

const CreateEditClient = () => {
  const navigate = useNavigate();
  const { client_id } = useParams();
  const isEditMode = !!client_id;

  const [value, setValue] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [successOpen, setSuccessOpen] = useState(false);

  const [clientEmail, setClientEmail] = useState("");
  const [clientLoginId, setClientLoginId] = useState("");
  const [credentialsReady, setCredentialsReady] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [clientFormData, setClientFormData] = useState(null);
  const [enableClientLogin, setEnableClientLogin] = useState(true);

  // For edit mode - initial data
  const [initialClientData, setInitialClientData] = useState(null);

  // For Save Changes button state
  const [clientInfoState, setClientInfoState] = useState({
    isValid: false,
    hasChanges: false,
    formData: null
  });

  const handleChange = (event, newValue) => setValue(newValue);
  // ❌ REMOVED: handleNext, handlePrev, tabLabels - No longer needed (Next button removed)

  const generateLoginId = () => {
    return "CLNT-" + Date.now();
  };

  // Load client data in edit mode
  useEffect(() => {
    if (isEditMode && client_id) {
      // Fetch client data from API
      const fetchClientData = async () => {
        try {
          const response = await fetch(
            `${config.API_BASE_URL_V1}/admin/client/details/${client_id}`,
            {
              method: "GET",
              headers: config.getHeaders(),
            }
          );

          const result = await response.json();
          console.log("Fetch client details response:", result);

          if (result.success && result.data) {
            const clientData = result.data;

            // Map backend data to frontend form structure
            const mappedData = {
              clientName: clientData.client_name || "",
              contactPersonName: clientData.contact_person || "",
              contactPersonPhone: clientData.mobile_number || "",
              contactPersonEmail: clientData.email_id || "",
              organizationPhoneNumber: clientData.organization_phone_number || "", // ✅ Prefill organization phone number
              organizationName: clientData.organization_name || "",
              address: clientData.address || "",
              loginId: clientData.client_id || "",
              enableLogin: clientData.status === "active",
            };

            setInitialClientData(mappedData);
            setClientEmail(mappedData.contactPersonEmail);
            setClientLoginId(mappedData.loginId);
            setEnableClientLogin(mappedData.enableLogin);
            setCredentialsReady(true);
          } else {
            console.error("Failed to fetch client data:", result.message);
            setSnackbarMsg(result.message || "Failed to load client data");
            setSnackbarOpen(true);
          }
        } catch (error) {
          console.error("Error fetching client data:", error);
          setSnackbarMsg(`Error loading client data: ${error.message}`);
          setSnackbarOpen(true);
        }
      };

      fetchClientData();
    }
  }, [isEditMode, client_id]);

  const handleValidationChange = (valid, formData) => {
    setIsFormValid(valid);
    setClientFormData(formData);
  };

  const handleCreateClient = async (enableLogin) => {
    // Prepare payload matching backend schema
    const payload = {
      client_name: clientFormData.clientName,
      organization_name: clientFormData.organizationName,
      contact_person: clientFormData.contactPersonName,
      email_id: clientFormData.contactPersonEmail, // Contact person email
      mobile_number: clientFormData.contactPersonPhone, // ✅ Contact person phone
      organization_phone_number: clientFormData.organizationPhoneNumber, // ✅ Organization phone number
      address: clientFormData.address,
      status: "active",
    };

    console.log("Creating client with payload:", payload);

    try {
      const response = await fetch(`${config.API_BASE_URL_V1}/admin/client/create`, {
        method: "POST",
        headers: config.getHeaders(),
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      console.log("Create client response:", data);

      if (data.success) {
        // Show success message with generated password
        const password = data.data?.generated_password;
        const clientId = data.data?.client_id;

        if (password) {
          setSnackbarMsg(
            `Client created successfully! Generated Password: ${password} (Please save this password)`
          );
        } else {
          setSnackbarMsg("Client created successfully!");
        }

        setSnackbarOpen(true);
        setSuccessOpen(true);

        // Navigate back after success
        setTimeout(() => {
          navigate("/client-management");
        }, 3000); // Longer timeout to allow user to see password
      } else {
        // ✅ Handle specific error codes
        const errorCode = data.error?.code;
        const errorDetails = data.error?.details;

        if (errorCode === "EMAIL_ALREADY_EXISTS") {
          setSnackbarMsg("Contact person email already exists. Please use a different email.");
        } else if (errorDetails && errorDetails.includes("duplicate key") && errorDetails.includes("client_user_email_id_key")) {
          // Fallback for database-level duplicate key errors
          setSnackbarMsg("Contact person email already exists. Please use a different email.");
        } else {
          setSnackbarMsg(data.message || "Failed to create client");
        }

        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("Error creating client:", error);
      setSnackbarMsg(`Error creating client: ${error.message}`);
      setSnackbarOpen(true);
    }
  };

  const handleSaveClientInfo = async (formData) => {
    try {
      console.log("Saving client info:", formData);

      // Map frontend form data to backend payload structure
      const payload = {
        client_name: formData.clientName,
        organization_name: formData.organizationName,
        contact_person: formData.contactPersonName,
        email_id: formData.contactPersonEmail,
        mobile_number: formData.contactPersonPhone,
        organization_phone_number: formData.organizationPhoneNumber, // ✅ Include organization phone number
        address: formData.address,
        modified_by: config.adminUserId || "admin",
      };

      const response = await fetch(
        `${config.API_BASE_URL_V1}/admin/client/update/${client_id}`,
        {
          method: "POST",
          headers: config.getHeaders(),
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();
      console.log("Update client response:", result);

      if (result.success) {
        setSnackbarMsg("Client information updated successfully!");
        setSnackbarOpen(true);

        // Update initial data to reflect saved changes
        setInitialClientData(formData);

        // Reset form state to indicate no unsaved changes
        setClientInfoState(prev => ({
          ...prev,
          hasChanges: false
        }));
      } else {
        // ✅ Handle specific error codes
        const errorCode = result.error?.code;
        const errorDetails = result.error?.details;

        if (errorCode === "EMAIL_ALREADY_EXISTS") {
          setSnackbarMsg("Contact person email already exists. Please use a different email.");
        } else if (errorDetails && errorDetails.includes("duplicate key") && errorDetails.includes("client_user_email_id_key")) {
          // Fallback for database-level duplicate key errors
          setSnackbarMsg("Contact person email already exists. Please use a different email.");
        } else {
          setSnackbarMsg(result.message || "Failed to update client information");
        }

        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("Error updating client info:", error);
      setSnackbarMsg(`Error updating client: ${error.message}`);
      setSnackbarOpen(true);
    }
  };

  const handleSaveCredentials = (credData) => {
    // This will be implemented when backend integration is done
    console.log("Saving credentials:", credData);
    setSnackbarMsg("Client credentials updated successfully!");
    setSnackbarOpen(true);
  };

  const handleResendOnboarding = async () => {
    try {
      console.log("Resending onboarding link to:", clientEmail);

      const response = await fetch(
        `${config.API_BASE_URL_V1}/admin/client/resend-email/${client_id}`,
        {
          method: "POST",
          headers: config.getHeaders(),
        }
      );

      const result = await response.json();
      console.log("Resend email response:", result);

      if (result.success) {
        setSnackbarMsg(
          `Onboarding email sent successfully to ${clientEmail}! A new password has been generated.`
        );
        setSnackbarOpen(true);
      } else {
        setSnackbarMsg(
          result.message || "Failed to send onboarding email"
        );
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("Error resending onboarding email:", error);
      setSnackbarMsg(`Error sending email: ${error.message}`);
      setSnackbarOpen(true);
    }
  };

  const handleToggleLogin = async (enabled) => {
    console.log("Toggling client login:", enabled);

    // ✅ Call backend API to toggle client status
    try {
      const response = await fetch(`${config.API_BASE_URL_V1}/admin/client/toggle-status/${client_id}`, {
        method: "POST",
        headers: config.getHeaders(),
        body: JSON.stringify({ enable_login: enabled }),
      });

      const data = await response.json();

      if (data.success) {
        setEnableClientLogin(enabled); // ✅ Update local state
        setSnackbarMsg(`Client login ${enabled ? "enabled" : "disabled"} successfully!`);
        setSnackbarOpen(true);
      } else {
        setSnackbarMsg(data.message || "Failed to update login status");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("Error toggling login:", error);
      setSnackbarMsg("Error updating login status. Please try again.");
      setSnackbarOpen(true);
    }
  };

  return (
    <Card
      sx={{
        boxShadow: "none",
        borderRadius: "7px",
        mb: "25px",
        padding: { xs: "18px", sm: "20px", lg: "25px" },
      }}
      className="rmui-card"
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: "25px",
        }}
      >
        <Typography
          variant="h3"
          sx={{ fontSize: { xs: "16px", md: "18px" }, fontWeight: 700 }}
        >
          {isEditMode ? "Edit Client" : "Add New Client"}
        </Typography>

        {/* Back button only in Edit Mode */}
        {isEditMode && (
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/client-management")}
            sx={{
              textTransform: "none",
              borderRadius: "7px",
              borderColor: "#D5D9E2",
              color: "#64748B",
              "&:hover": {
                borderColor: "#605DFF",
                backgroundColor: "#ECF0FF",
              },
            }}
          >
            Back
          </Button>
        )}
      </Box>

      {/* Tabs */}
      <Tabs
        value={value}
        onChange={handleChange}
        sx={{
          "& .MuiTab-root": {
            textTransform: "none",
            fontSize: "14px",
            fontWeight: 500,
          },
          "& .Mui-selected": {
            color: "#605DFF",
          },
          "& .MuiTabs-indicator": {
            backgroundColor: "#605DFF",
          },
        }}
      >
        <Tab label="Client Information" {...a11yProps(0)} />
        <Tab label="Client Credentials" {...a11yProps(1)} disabled={!isEditMode} />
      </Tabs>

      {/* Create Mode - Only Client Info Tab */}
      {!isEditMode ? (
        <CustomTabPanel value={value} index={0}>
          <ClientInfo
            setClientEmail={setClientEmail}
            onValidationChange={handleValidationChange}
            isEditMode={false}
            initialData={null}
            onSaveChanges={null}
          />

          {/* Submit Button */}
          <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleCreateClient(true)}
              disabled={!isFormValid}
              sx={{
                textTransform: "none",
                borderRadius: "7px",
                padding: "10px 30px",
                fontSize: "14px",
                fontWeight: 500,
                backgroundColor: "#605DFF",
                "&:hover": {
                  backgroundColor: "#3e2ad8",
                },
                "&:disabled": {
                  backgroundColor: "#B1BBC8",
                  color: "#fff",
                },
              }}
            >
              Submit
            </Button>
          </Box>
        </CustomTabPanel>
      ) : (
        /* Edit Mode - Tabs with Client Info and Credentials */
        <>
          <CustomTabPanel value={value} index={0}>
            <ClientInfo
              setClientEmail={setClientEmail}
              onValidationChange={handleValidationChange}
              isEditMode={true}
              initialData={initialClientData}
              onFormStateChange={setClientInfoState}
            />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={1}>
            <ClientCredentials
              email={clientEmail}
              loginId={clientLoginId}
              credentialsReady={credentialsReady}
              isEditMode={true}
              initialEnableLogin={enableClientLogin}
              onCreateClient={null}
              onSaveChanges={handleSaveCredentials}
              onResendOnboarding={handleResendOnboarding}
              onToggleLogin={handleToggleLogin}
            />
          </CustomTabPanel>

          {/* Save Changes Button - Only in Edit Mode on Client Info tab */}
          {value === 0 && (
            <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                onClick={() => handleSaveClientInfo(clientInfoState.formData)}
                disabled={!clientInfoState.isValid || !clientInfoState.hasChanges}
                sx={{
                  textTransform: "none",
                  borderRadius: "7px",
                  padding: "10px 30px",
                  fontSize: "14px",
                  fontWeight: 500,
                  backgroundColor: "#605DFF",
                  "&:hover": {
                    backgroundColor: "#3e2ad8",
                  },
                  "&:disabled": {
                    backgroundColor: "#B1BBC8",
                    color: "#fff",
                  },
                }}
              >
                Save Changes
              </Button>
            </Box>
          )}
          {/* ❌ REMOVED: Next button - Users can navigate using tabs directly */}
        </>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMsg}
        autoHideDuration={3000}
        TransitionComponent={Fade}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />

      <SuccessDialog
        open={successOpen}
        onClose={() => {
          setSuccessOpen(false);
        }}
      />
    </Card>
  );
};

export default CreateEditClient;
